<template>
  <v-card :elevation="elevation" :loading="loading" :disabled="loading">
    <v-toolbar v-if="!hideToolbar" flat>
      <v-toolbar-title class="grey--text">{{ title }}</v-toolbar-title>

      <div class="flex-grow-1"></div>

      <v-btn
        v-if="canFilter && userFilters.length > 0"
        text
        color="primary"
        @click="removeFilter"
      >
        <v-icon>mdi-filter-remove-outline</v-icon>
        <span>{{ t_removeFilter }}</span>
      </v-btn>

      <UserFilterPicker
        v-if="canFilter"
        :value="userFilters"
        :config="config"
        @input="filterUpdated"
      />

      <v-btn
        v-if="canInsert"
        text
        :title="t_add"
        color="primary"
        @click="addItem()"
      >
        <v-icon>mdi-plus</v-icon>
        <span class="hidden-sm-and-down ml-1">{{ t_add }}</span>
      </v-btn>

      <v-btn
        v-for="(item, index) in toolbarButtons"
        :key="index"
        text
        @click="$emit('toolbar-button-click', item)"
        :title="item.title"
        :color="getBtnColor(item.color)"
      >
        <v-icon v-if="item.icon">{{ item.icon }}</v-icon>
        <span class="hidden-sm-and-down ml-1">{{ item.title }}</span>
      </v-btn>
    </v-toolbar>
    <v-divider v-if="!hideToolbar"></v-divider>

    <v-card-text class="pa-0">
      <v-data-table
        :hide-default-footer="!showPaging"
        :headers="tableColumns"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :disable-pagination="!showPaging"
        :footer-props="footerProps"
      >
        <template v-slot:item="{ item, headers }">
          <tr>
            <td v-for="header in headers" :key="header.value">
              <div v-if="header.value == 'actions'" :style="actionWidth">
                <v-btn
                  v-for="b in rowButtons"
                  :key="b.text"
                  small
                  class="mr-2"
                  @click="$emit('row-button-click', { item: item, button: b })"
                  icon
                  :title="b.text"
                  :color="b.color ? b.color : 'primary'"
                >
                  <v-icon>{{ b.icon }}</v-icon>
                </v-btn>
                <v-btn
                  v-if="canUpdate"
                  small
                  class="mr-2"
                  @click="editItem(item)"
                  icon
                  title="Edit"
                  color="primary"
                >
                  <v-icon>mdi-square-edit-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="canDelete"
                  small
                  @click="deleteItem(item)"
                  icon
                  title="Delete"
                  color="error"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
              <div v-else>
                <component
                  v-bind:is="header.type"
                  v-model="item[header.field]"
                  :config="header"
                ></component>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="formDialog" scrollable width="800" :retain-focus="false">
      <v-card :loading="formLoading" :disabled="formLoading">
        <v-card-title>{{ formTitle }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form v-model="formValid">
            <FormView :config="config" v-model="edtItem" />
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn
            color="primary"
            text
            @click="saveItem()"
            :disabled="!formValid"
            >{{ t_save }}</v-btn
          >
          <v-btn color="error" text @click="formDialog = false">{{
            t_cancel
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import server from "../../../services/server";
import { mapGetters } from "vuex";
import viewers from "../controls/index_viewer";
import FormView from "./FormView.vue";
import UserFilterPicker from "./UserFilterPicker.vue";
import ui from "../../../services/data";
import constants from "../../../helpers/constants";

export default {
  name: "ApiDataTable",

  components: { FormView, UserFilterPicker, ...viewers.types },

  props: [
    "dataApi",
    "modelApi",
    "queryApi",
    "insertApi",
    "updateApi",
    "deleteApi",
    "title",
    "toolbarButtons",
    "rowButtons",
    "params",
    "showPaging",
    "canInsert",
    "canUpdate",
    "canDelete",
    "canFilter",
    "elevation",
    "hideToolbar",
    "filters",
    "useDatasApi",
  ],

  data() {
    return {
      loading: false,
      lastOptions: null,
      edtItem: null,
      formDialog: false,
      formLoading: false,
      formValid: false,
      items: [],
      options: {},
      footerProps: {},
      totalItems: 0,
      lastQueryOptions: null,
      cRowIndex: 0,
      config: null,
      userFilters: [],
    };
  },

  computed: {
    keyName() {
      return this.config ? this.config.keyName : "";
    },
    tableColumns() {
      if (!this.config || !this.config.children) return [];

      var fields = [];

      this.config.children.forEach((s) => {
        s.children.forEach((c) => {
          if (!c.hideInList) {
            var cc = Object.assign({}, c);
            fields.push(cc);
            cc.text = c.title;
            cc.value = c.field;
            cc.type = c.type + "View";
          }
        });
      });

      fields.push({
        text: "",
        value: "actions",
        sortable: false,
        width: "100px",
      });

      return fields;
    },
    formTitle() {
      if (this.edtItem && !this.edtItem[this.keyName]) return this.t_add;
      return this.t_edit;
    },
    actionWidth() {
      var n = 0;
      if (this.canUpdate) n++;
      if (this.canDelete) n++;
      if (this.rowButtons) n += this.rowButtons.length;

      if (n > 0) return "width:" + n * 35 + "px";
      return "";
    },
    ...mapGetters([
      "t_add",
      "t_edit",
      "t_deleteConfirm",
      "t_save",
      "t_cancel",
      "t_removeFilter",
    ]),
  },

  watch: {
    options: {
      handler() {
        this.refresh();
      },
      deep: true,
    },
    modelApi() {
      this.refresh(true);
    },
    dataApi() {
      this.refresh(true);
    },
    params() {
      this.refresh(true);
    },
    filters() {
      this.refresh(true);
    },
  },

  methods: {
    beginLoad() {
      this.loading = true;
      this.formLoading = true;
    },
    endLoad() {
      this.loading = false;
      this.formLoading = false;
    },
    async refresh(force) {
      if (this.loading) return;

      this.beginLoad();

      if (force) this.lastQueryOptions = null;

      if (this.config == null || force) {
        var modelApi;
        if (this.modelApi) modelApi = this.modelApi;
        else if (this.dataApi) modelApi = this.dataApi + "/model";
        else return;

        this.config = await this.$store.dispatch("cache/getObject", {
          apiName: modelApi,
        });
      }

      var opt = {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        sortBy: this.options.sortBy.length ? this.options.sortBy[0] : null,
        sortDesc: this.options.sortDesc.length
          ? this.options.sortDesc[0]
          : false,
        requireTotalRows:
          this.lastQueryOptions == null ||
          this.lastQueryOptions.itemsPerPage != this.options.itemsPerPage,
        filters: [],
      };

      if (this.filters) {
        opt.filters = [...this.filters];
      }

      if (this.userFilters.length > 0) {
        for (let i = 0; i < this.userFilters.length; i++) {
          const uf = this.userFilters[i];
          opt.filters.push(uf);
        }
      }

      if (this.params) {
        for (const key in this.params) {
          opt.filters.push({
            name: key,
            compare: "=",
            value: this.params[key],
          });
        }
      }

      this.lastQueryOptions = opt;

      var api;

      if (this.dataApi) {
        api = this.dataApi + "/query";
      } else {
        api = this.queryApi;
      }

      if (!this.showPaging) opt.itemsPerPage = 500;

      try {
        var response = await server.post(api, opt);

        if (this.lastQueryOptions.requireTotalRows)
          this.totalItems = response.totalRows;
        this.items = response.rows;
      } catch (err) {
        alert(err);
      }

      this.endLoad();
    },
    addItem() {
      if (!this.config || !this.config.children) return;
      this.edtItem = ui.getNewRowData(this.config);
      this.formDialog = true;
    },
    editItem(item) {
      this.edtItem = Object.assign({}, item);
      this.cRowIndex = this.items.indexOf(item);
      this.formDialog = true;
    },
    saveItem() {
      var api;

      if (this.params) {
        for (const key in this.params) {
          this.edtItem[key] = this.params[key];
        }
      }

      if (this.edtItem[this.config.keyName]) {
        this.beginLoad();

        if (this.dataApi) {
          if (this.useDatasApi) api = this.dataApi + "/update";
          else api = this.dataApi;
        } else {
          api = this.updateApi;
        }

        server
          .put(api, this.edtItem[this.config.keyName], this.edtItem)
          .then((response) => {
            if (response) {
              for (const key in response) {
                if (Object.hasOwnProperty.call(response, key)) {
                  const e = response[key];
                  this.edtItem[key] = e;
                }
              }
            }

            this.$set(this.items, this.cRowIndex, this.edtItem);
            this.formDialog = false;
          })
          .catch((err) => {
            alert(err);
          })
          .finally(() => this.endLoad());
      } else {
        this.beginLoad();

        if (this.dataApi) {
          if (this.useDatasApi) api = this.dataApi + "/insert";
          else api = this.dataApi;
        } else {
          api = this.insertApi;
        }

        server
          .post(api, this.edtItem)
          .then((response) => {
            this.items.push(response);
            this.totalItems++;
            this.formDialog = false;
          })
          .catch((err) => {
            alert(err);
          })
          .finally(() => this.endLoad());
      }
    },
    deleteItem(row) {
      if (!confirm(this.t_deleteConfirm)) return;

      var api;

      if (this.dataApi) {
        if (this.useDatasApi) api = this.dataApi + "/delete";
        else api = this.dataApi;
      } else {
        api = this.deleteApi;
      }

      var rowId;

      if (this.config.keyName) rowId = row[this.config.keyName];

      if (!rowId) {
        rowId = row[constants.CLIENT_ID_FIELD];

        if (!rowId) {
          alert("Invalid row ID!");
          return;
        }
      }

      this.beginLoad();

      server
        .delete(api, rowId)
        .then(() => {
          var n = this.items.indexOf(row);
          this.items.splice(n, 1);
          this.totalItems--;
        })
        .catch((err) => {
          alert(err);
        })
        .finally(() => this.endLoad());
    },
    filterUpdated(filters) {
      this.userFilters = [...filters];
      this.refresh();
    },
    removeFilter() {
      this.userFilters = [];
      this.refresh();
    },
    getBtnColor(s) {
      return s ? s : "primary";
    },
    setRows(rows, totalRows) {
      if (this.lastQueryOptions.requireTotalRows) this.totalItems = totalRows;
      this.items = rows;
    },
  },
};
</script>